import { LanguageCode } from 'i18n/internationalization.types';
import {
  CustomTemplateWithLocales,
  MessagingType,
} from 'models/automation/CustomTemplates';
import * as Yup from 'yup';
import { OptionalObjectSchema } from 'yup/lib/object';

const perLanguageSchema = ({
  messagingType,
  requireFields = true,
}: {
  messagingType: MessagingType;
  requireFields?: boolean;
}) => {
  const contentSchema = requireFields
    ? Yup.string().richTextEditorContentRequired()
    : Yup.string().optional().nullable();

  const contentHtmlContentSchema = requireFields
    ? Yup.string().required()
    : Yup.string().optional().nullable();

  const subjectSchema = Yup.string().when({
    is: () => messagingType === MessagingType.EMAIL && requireFields,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional().nullable(),
  });

  return Yup.object({
    content: contentSchema,
    content_htmlContent: contentHtmlContentSchema,
    subject: subjectSchema,
  }).nullable();
};

export const customTemplateAddEditFormSchema = ({
  availableLanguages,
  messagingType,
  requireAllLanguages = false,
}: {
  availableLanguages: LanguageCode[];
  messagingType: MessagingType;
  requireAllLanguages?: boolean;
}) => ({
  uid: Yup.string().nullable().optional(),
  name: Yup.string().required(),
  messagingType: Yup.mixed<MessagingType>()
    .oneOf(Object.values(MessagingType))
    .required(),
  language: Yup.string().required(),
  locales: Yup.object({
    ...availableLanguages.reduce(
      (acc, language) => ({
        ...acc,
        [language]:
          language === LanguageCode.EN || requireAllLanguages
            ? perLanguageSchema({
                messagingType,
                requireFields: true,
              }).required()
            : perLanguageSchema({
                messagingType,
                requireFields: false,
              }).optional(),
      }),
      {} as {
        [key in LanguageCode]: ReturnType<typeof perLanguageSchema>;
      },
    ),
  }),
});

export type CustomTemplateAddEditFormValues = Yup.InferType<
  OptionalObjectSchema<ReturnType<typeof customTemplateAddEditFormSchema>>
>;

export const getFormDefaultValues = ({
  customTemplate,
  templateType,
  availableLanguages,
  currentLanguage,
}: {
  customTemplate?: CustomTemplateWithLocales;
  templateType?: MessagingType;
  availableLanguages: LanguageCode[];
  currentLanguage: LanguageCode;
}): CustomTemplateAddEditFormValues => {
  return {
    uid: customTemplate?.uid ?? null,
    name: customTemplate?.name ?? '',
    // @ts-expect-error we will always have either the template or just templateType
    messagingType: customTemplate?.messagingType ?? templateType,
    language: currentLanguage,
    locales: availableLanguages.reduce((acc, language) => {
      const localeContents = customTemplate?.locales[language];

      return {
        ...acc,
        [language]: {
          content: localeContents?.content ?? '<p></p>',
          content_htmlContent: localeContents?.content ?? '<p></p>',
          subject: localeContents?.subject ?? '',
        },
      };
    }, {} as CustomTemplateAddEditFormValues['locales']),
  };
};
